import { API, deleteRequest, getRequest, patchRequest } from '@/api/api.ts';
import type { ChatMessage } from '@/store/messages/messages.dto.ts';
import { generateSearchParams } from '@/utils/generate-search-params.ts';
import { type CursorPage, type CursorPageDto, toCursorPage } from '@/utils/list.ts';

import type { BidRoom, ChatRoom } from './chat.dto.ts';

type CrewChatTypes = {
  created_at: string;
  crew_chat_id: string;
  service_id: string;
  updated_at: string;
};

export const getVesselCrewChats = async () => {
  return await getRequest<CrewChatTypes>(API.PEOPLE, '/vessel-crew-chats');
};

export interface RoomsFilters {
  search?: string;
  topic_code?: string;
  type?: string;
  with_user?: string;
}

/** One step of the rooms stream: pinned first, then by last activity. */
export const getRoomsByCursor = async <T extends ChatRoom | BidRoom = ChatRoom>(
  limit: number,
  cursor: string,
  filters: RoomsFilters = {},
): Promise<CursorPage<T>> => {
  const params = generateSearchParams({
    limit,
    // Empty is "start at the beginning" — the first step sends no cursor at all.
    ...(cursor && { cursor }),
    search: filters.search || undefined,
    topic_code: filters.topic_code,
    type: filters.type,
    with_user: filters.with_user,
  });
  const { data } = await getRequest<CursorPageDto<T>>(API.CHAT, `/rooms?${params}`);
  return toCursorPage(data);
};

// The first step only has to fill the screen; the rest of the walk takes bigger steps. The cursor
// is not bound to the step size, so it can change mid-walk.
const ROOMS_FIRST_STEP = 30;
const ROOMS_STEP = 200;

/**
 * Every room matching the filters, walked step by step. The cursor is bound to
 * the filters it was issued for, so they stay the same for the whole walk.
 * `onStep` sees the rooms gathered so far after every step, so a list can render
 * before the walk ends.
 */
export const getAllRooms = async (
  filters: RoomsFilters = {},
  onStep?: (rooms: ChatRoom[]) => void,
): Promise<ChatRoom[]> => {
  const rooms: ChatRoom[] = [];
  let cursor = '';
  for (;;) {
    const page = await getRoomsByCursor(cursor ? ROOMS_STEP : ROOMS_FIRST_STEP, cursor, filters);
    rooms.push(...page.items);
    if (!page.hasMore || !page.nextCursor) return rooms;
    onStep?.([...rooms]);
    cursor = page.nextCursor;
  }
};

/** The service-request rooms opened against a task — `topicCode` is the task id. */
export const getBidRooms = async (topicCode: string) => {
  const page = await getRoomsByCursor<BidRoom>(10, '', { type: 'bid', topic_code: topicCode });
  return page.items;
};

export interface RoomMessagesFilters {
  text?: string;
}

/** One step of a room's message stream, newest first. */
export const getRoomMessages = async (
  roomId: string,
  limit = 100,
  cursor = '',
  filters: RoomMessagesFilters = {},
): Promise<CursorPage<ChatMessage>> => {
  const params = generateSearchParams({
    limit,
    ...(cursor && { cursor }),
    text: filters.text || undefined,
  });
  const { data } = await getRequest<CursorPageDto<ChatMessage>>(
    API.CHAT,
    `/rooms/${roomId}/messages?${params}`,
  );
  return toCursorPage(data);
};

export const removeRoomUser = (roomId: string, userId: string) =>
  deleteRequest<ChatRoom, { user_id: string }>(API.CHAT, `/rooms/${roomId}/users`, {
    user_id: userId,
  });

export const updateTemporaryRoomDeletionDate = (
  roomId: string,
  galleryId: string,
  removeAfter: Date,
) => {
  return patchRequest<ChatRoom, { gallery_id: string; remove_after: string }>(
    API.CHAT,
    `/rooms/${roomId}/temporary`,
    {
      gallery_id: galleryId,
      remove_after: removeAfter.toISOString(),
    },
  );
};
